import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { withInject, errorBoundary } from '@piwikpro/platform';
import {
  Layout,
  Stack,
  withTranslation,
  Card,
  Image,
  Text,
  ThemeContext,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';
import { Notifications } from '@piwikpro/notification-crate';
import { ContainerSettingsState } from '@piwikpro/container-settings-crate/redux';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import {
  IOSInstall,
  AndroidInstall,
  ReactNativeInstall,
  FlutterInstall,
} from '@piwikpro/apps-crate/components/InstallationMethods';
import IOSIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/ios.svg';
import IOSIconDark from '@piwikpro/apps-crate/components/InstallationMethods/icons/ios-dark.svg';
import AndroidIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/android.svg';
import ReactNativeIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/react.svg';
import FlutterIcon from '@piwikpro/apps-crate/components/InstallationMethods/icons/flutter.svg';
import { AdministrationState, STATE_ROOT } from '../../../../../redux';


const mapStateToProps = (state: AdministrationState & ContainerSettingsState) => ({
  app: state[STATE_ROOT].apps.details.settings.settings!,
  trackingDomain: state.containerSettings.app.domainSettings.trackingDomain.value as string,
  trackingDomainFailed: state.containerSettings.app.domainSettings.trackingDomain.failed,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  notifications: Notifications
  productAnalytics: ProductAnalytics
  Title: typeof React.Component
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

class MobileApps extends React.Component<Props> {
  static context = ThemeContext;

  constructor(props: Props) {
    super(props);

    this.handleCopyToClipboard = this.handleCopyToClipboard.bind(this);
  }

  handleCopyToClipboard = (result, productAnalyticsActionType) => {
    const { notifications, productAnalytics, t } = this.props;

    if (result) {
      notifications.push({
        text: t('components:notifications.copied.success'),
        type: 'success',
      });
      productAnalytics.push({ type: `${productAnalyticsActionType}/copy` });
    } else {
      notifications.push({
        text: t('components:notifications.copied.error'),
        type: 'error',
      });
    }
  };

  render() {
    const {
      t, Title, app, trackingDomain, trackingDomainFailed, productAnalytics,
    } = this.props;

    return (
      <Title part={t('apps.details.tabs.mobile.apps.installation')}>
        <Layout>
          <Stack vertical>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={this.context === 'dark' ? IOSIconDark : IOSIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.ios.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({ type: '@platform/command/appInstallation/ios' });
                    }
                  }}
                >
                  <IOSInstall
                    app={app}
                    t={t}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={AndroidIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.android.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/android',
                      });
                    }
                  }}
                >
                  <AndroidInstall
                    app={app}
                    t={t}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={ReactNativeIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.react-native.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/reactNative',
                      });
                    }
                  }}
                >
                  <ReactNativeInstall
                    app={app}
                    trackingDomain={trackingDomain}
                    trackingDomainFailed={trackingDomainFailed}
                    t={t}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
            <Stack.Item fill>
              <Card>
                <Card.Accordion
                  clickableContent={(
                    <Stack verticalAlignment="center">
                      <Stack.Item>
                        <Image src={FlutterIcon} displayType="block" />
                      </Stack.Item>
                      <Stack.Item fill wrap>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                          {t('apps.details.installation.flutter.title')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  )}
                  clickableContentOnClick={(isOpened) => {
                    if (isOpened) {
                      productAnalytics.push({
                        type: '@platform/command/appInstallation/flutter',
                      });
                    }
                  }}
                >
                  <FlutterInstall
                    app={app}
                    trackingDomain={trackingDomain}
                    trackingDomainFailed={trackingDomainFailed}
                    t={t}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                  />
                </Card.Accordion>
              </Card>
            </Stack.Item>
          </Stack>
        </Layout>
      </Title>
    );
  }
}

export default connector(withInject<InjectedProps>({
  productAnalytics: 'productAnalytics',
  notifications: 'NotificationCrate.notifications',
  Title: 'TitleCrate.components.Title',
})(
  withTranslation('administration')(errorBoundary(MobileApps)),
));
